import {
  useClientsByIds,
  useClientsInfiniteQuery,
  useInView,
} from "creso-core/hooks";
import {
  forwardRef,
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useDebounceValue } from "usehooks-ts";
import {
  CommandList,
  CommandInput,
  PopoverContent,
  Button,
  PopoverTrigger,
  Popover,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "web-design-system";
import { Loader2, ChevronsUpDown, XIcon } from "lucide-react";
import { getInitials } from "creso-core/utils";
import { Avatar } from "web-core";
import { Client, ClientStatus } from "creso-core/types";

export type ClientSearchSelection = Pick<Client, "id" | "name">;

export type ClientSearchTriggerProps = {
  onClear: (e: React.MouseEvent) => void;
  client?: ClientSearchSelection;
  onClick: () => void;
  open: boolean;
  placeholder?: string;
  display: string;
};

const ClientSeachTrigger = forwardRef<HTMLDivElement, ClientSearchTriggerProps>(
  (props, ref) => {
    const { open, client, onClear, display, onClick } = props;
    return (
      <div ref={ref} className="relative w-full">
        <Button
          variant="outline"
          role="combobox"
          onClick={onClick}
          aria-expanded={open}
          className="w-full text-left justify-between shadow-none pr-8"
        >
          <span className="min-w-0 truncate">{display}</span>
        </Button>
        {client ? (
          <XIcon
            className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 cursor-pointer"
            onClick={onClear}
          />
        ) : (
          <ChevronsUpDown
            onClick={onClick}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 cursor-pointer"
          />
        )}
      </div>
    );
  }
);
ClientSeachTrigger.displayName = "ClientSeachTrigger";

export function ClientSearch({
  onSelect,
  status = [],
  clientIds,
  options,
  placeholder,
  trigger,
  client,
  showUnassign = false,
}: {
  onSelect: (client?: Client) => void;
  status?: ClientStatus[];
  clientIds?: number[];
  options?: {
    portal?: boolean;
    modal?: boolean;
    align?: "start" | "center" | "end";
  };
  trigger?: (props: ClientSearchTriggerProps) => React.ReactNode;
  placeholder?: string;
  /** Optional. Pass the currently selected client to control the trigger's
   * display and clear (X) button, instead of relying on `placeholder` to
   * fake the selected name. Leave unset for fully uncontrolled usage. */
  client?: ClientSearchSelection | null;
  /** Replaces the trigger's clear (X) button with an "Unassign" row docked
   * to the bottom of the option list. Defaults to false. */
  showUnassign?: boolean;
}) {
  const { portal = true, modal = false, align = "center" } = options ?? {};
  const [search, setSearch] = useState("");

  const [selectedClient, setSelectedClient] = useState<
    ClientSearchSelection | undefined
  >(client ?? undefined);

  useEffect(() => {
    if (client !== undefined) setSelectedClient(client ?? undefined);
  }, [client]);

  const [deboucedSearch] = useDebounceValue(search, 500);

  const [open, setOpen] = useState(false);

  const [highlightedValue, setHighlightedValue] = useState("");

  const fetchClientsById = Boolean(clientIds?.length);

  const { clients: clientsById, isLoading: isClientsByIdLoading } =
    useClientsByIds(fetchClientsById ? clientIds! : []);

  const {
    clients: queriedClients,
    hasNextPage,
    fetchNextPage,
  } = useClientsInfiniteQuery(deboucedSearch, { status }, undefined, {
    enabled: !fetchClientsById,
  });

  const clients = useMemo(() => {
    if (!fetchClientsById) return queriedClients;

    const query = deboucedSearch.trim().toLowerCase();
    if (!query) return clientsById;

    return clientsById.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.phone_number?.toLowerCase().includes(query)
    );
  }, [fetchClientsById, queriedClients, clientsById, deboucedSearch]);

  const showInfiniteLoader = !fetchClientsById && hasNextPage;

  const onSelectRef = useRef(onSelect);

  onSelectRef.current = onSelect;

  const ref = useRef<HTMLDivElement>(null);

  const inView = useInView(ref);

  useEffect(() => {
    if (!fetchClientsById && inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, fetchClientsById]);

  const handleSelect = useCallback((client: Client) => {
    setSelectedClient(client);
    setOpen(false);
    setSearch("");
    onSelectRef.current(client);
  }, []);

  const handleUnassign = useCallback(() => {
    setSelectedClient(undefined);
    setOpen(false);
    setSearch("");
    onSelectRef.current(undefined);
  }, []);

  const Trigger = useMemo(() => trigger || ClientSeachTrigger, [trigger]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <Trigger
          onClear={(e) => {
            e.stopPropagation();
            setSelectedClient(undefined);
            onSelectRef.current(undefined);
          }}
          onClick={() => setOpen((o) => !o)}
          open={open}
          placeholder={placeholder}
          client={showUnassign ? undefined : selectedClient}
          display={selectedClient?.name ?? (placeholder || "Select client")}
        />
      </PopoverTrigger>
      <PopoverContent
        align={align}
        portal={portal}
        className="p-0 w-(--radix-popover-trigger-width) max-w-(--radix-popover-trigger-width)"
      >
        <Command
          shouldFilter={false}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
        >
          <CommandInput
            value={search}
            onValueChange={(v) => setSearch(v)}
            placeholder="Search client..."
          />
          <CommandList>
            <CommandEmpty>No client found</CommandEmpty>
            <CommandGroup>
              {fetchClientsById && isClientsByIdLoading ? (
                <div className="flex justify-center items-center w-full py-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : null}
              {clients?.map((client) => (
                <CommandItem
                  className="flex flex-row justify-start gap-4 items-center"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(client);
                    setSearch("");
                  }}
                  onSelect={() => handleSelect(client)}
                  key={client.id}
                  id={client.id.toString()}
                  itemID={client.id.toString()}
                  value={client.id.toString()}
                >
                  <Avatar
                    url={""}
                    fallback={getInitials(client.name)}
                    size="sm"
                  />
                  <div className="flex flex-col justify-start items-start">
                    <p className="text-sm font-medium line-clamp-2">
                      {client.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {client.phone_number}
                    </p>
                  </div>
                </CommandItem>
              ))}
              {showInfiniteLoader && (
                <div
                  ref={ref}
                  className="flex justify-center items-center w-full py-2"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
        {showUnassign && selectedClient && (
          <button
            type="button"
            onClick={handleUnassign}
            onMouseEnter={() => setHighlightedValue("")}
            className="relative flex w-full cursor-default select-none items-center font-medium gap-2 border-t px-4 py-3 text-sm rounded-b-lg text-muted-foreground outline-none hover:bg-accent hover:text-accent-foreground"
          >
            Unassign
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
